import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Link, useLocation, useParams, useNavigate } from 'react-router-dom';
import { Menu, X, ChevronDown, Phone, Mail, MapPin, Clock, MessageCircle, ArrowRight, Check, Users, Briefcase, Building2, Factory, Banknote, ChevronRight } from 'lucide-react';
import axios from 'axios';
import './App.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Logo Component
const Logo = () => (
  <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
    <img 
      src="https://customer-assets.emergentagent.com/job_funny-bell-3/artifacts/fyxxdk9h_WhatsApp%20Image%202026-02-19%20at%2001.46.35.jpeg" 
      alt="FMF Global Logo" 
      className="h-10 w-10 object-contain"
    />
    <span className="font-sans font-bold text-white tracking-wider text-sm md:text-base">FMF GLOBAL CONSULTANCY</span>
  </Link>
);

// Navigation Component
const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'About', path: '/about' },
    { name: 'Services', path: '/services', hasDropdown: true },
    { name: 'Team', path: '/team' },
    { name: 'Blog', path: '/blog' },
    { name: 'Contact', path: '/contact' },
  ];

  const serviceLinks = [
    { name: 'Strategic Advisory', path: '/services#advisory' },
    { name: 'Audit & Tax', path: '/services#tax' },
    { name: 'Corporate Services', path: '/services#corporate' },
    { name: 'Industrial & Manufacturing', path: '/services#manufacturing' },
    { name: 'Finance & Fund-Raising', path: '/services#finance' },
  ];

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-[#1A1A1C]/95 backdrop-blur-md shadow-lg' : 'bg-transparent'}`} data-testid="main-navigation">
      <div className="container-custom">
        <div className="flex items-center justify-between h-20">
          <Logo />
          
          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <div key={link.name} className="relative group">
                {link.hasDropdown ? (
                  <button
                    className={`nav-link flex items-center gap-1 text-sm font-medium ${location.pathname === link.path ? 'text-[#C9A962]' : 'text-white hover:text-[#C9A962]'}`}
                    onMouseEnter={() => setServicesOpen(true)}
                    onMouseLeave={() => setServicesOpen(false)}
                    data-testid={`nav-${link.name.toLowerCase()}`}
                  >
                    {link.name}
                    <ChevronDown size={14} />
                  </button>
                ) : (
                  <Link
                    to={link.path}
                    className={`nav-link text-sm font-medium ${location.pathname === link.path ? 'text-[#C9A962]' : 'text-white hover:text-[#C9A962]'}`}
                    data-testid={`nav-${link.name.toLowerCase()}`}
                  >
                    {link.name}
                  </Link>
                )}
                
                {link.hasDropdown && (
                  <div
                    className={`absolute top-full left-0 mt-2 w-64 bg-[#232325] border border-[#2D2D30] shadow-xl transition-all duration-200 ${servicesOpen ? 'opacity-100 visible' : 'opacity-0 invisible'}`}
                    onMouseEnter={() => setServicesOpen(true)}
                    onMouseLeave={() => setServicesOpen(false)}
                  >
                    {serviceLinks.map((service) => (
                      <Link
                        key={service.name}
                        to={service.path}
                        className="block px-4 py-3 text-sm text-gray-300 hover:text-[#C9A962] hover:bg-[#2D2D30] transition-colors"
                      >
                        {service.name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
            <Link to="/contact" className="btn-outline-gold text-sm" data-testid="nav-book-consultation">
              Book Consultation
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden text-white"
            onClick={() => setIsOpen(!isOpen)}
            data-testid="mobile-menu-toggle"
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div className="lg:hidden bg-[#1A1A1C] border-t border-[#2D2D30]">
            {navLinks.map((link) => (
              <Link
                key={link.name}
                to={link.path}
                className="block px-4 py-3 text-white hover:text-[#C9A962] hover:bg-[#232325]"
                onClick={() => setIsOpen(false)}
              >
                {link.name}
              </Link>
            ))}
            <div className="p-4">
              <Link to="/contact" className="btn-gold block text-center" onClick={() => setIsOpen(false)}>
                Book Consultation
              </Link>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

// WhatsApp Float Button
const WhatsAppFloat = () => (
  <a
    href="https://wa.me/971548887455?text=Hello%20FMF%20Global%20Consultancy,%20I%20would%20like%20to%20discuss%20a%20consultation."
    target="_blank"
    rel="noopener noreferrer"
    className="whatsapp-float"
    data-testid="whatsapp-float"
    aria-label="Contact us on WhatsApp"
  >
    <svg viewBox="0 0 24 24" width="28" height="28" fill="white">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
    </svg>
  </a>
);

// Footer Component
const Footer = () => (
  <footer className="bg-[#0f0f10] py-8 border-t border-[#2D2D30]" data-testid="footer">
    <div className="container-custom">
      <div className="flex flex-col md:flex-row items-center justify-center gap-4">
        <p className="text-gray-500 text-sm">&copy; 2026 FMF Global Consultancy. All rights reserved. | <a href="https://fmfglobalconsultancy.com" className="hover:text-[#C9A962]">fmfglobalconsultancy.com</a></p>
      </div>
    </div>
  </footer>
);

// Hero Section for Pages
const PageHero = ({ label, title, description }) => (
  <section className="relative min-h-[60vh] flex items-center" data-testid="page-hero">
    <div className="absolute inset-0">
      <img
        src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=1920&q=80"
        alt="Dubai Skyline"
        className="w-full h-full object-cover"
      />
      <div className="absolute inset-0 hero-overlay"></div>
    </div>
    <div className="container-custom relative z-10 pt-20">
      <p className="text-[#C9A962] tracking-[0.2em] text-sm mb-4">{label}</p>
      <h1 className="font-serif text-4xl md:text-6xl lg:text-7xl text-white mb-6 max-w-4xl">{title}</h1>
      <p className="text-gray-300 text-lg md:text-xl max-w-2xl">{description}</p>
    </div>
  </section>
);

// Home Page
const HomePage = () => {
  const services = [
    { icon: <Users size={24} />, title: 'Strategic Advisory', desc: 'Virtual CFO, AI seminars, business restructuring, cost optimization, and board advisory services.', link: '/services#advisory' },
    { icon: <Briefcase size={24} />, title: 'Audit & Tax Consultants', desc: 'VAT, corporate tax compliance, ICV certification, and strategic CFO advisory services.', link: '/services#tax' },
    { icon: <Building2 size={24} />, title: 'Corporate Services', desc: 'Company formation, visa & immigration, PRO services, and strategic business consulting.', link: '/services#corporate' },
    { icon: <Factory size={24} />, title: 'Industrial & Manufacturing', desc: 'Expertise in Float Glass, Processing plants, Aluminium extrusions, fabrications and industrial projects.', link: '/services#manufacturing' },
    { icon: <Banknote size={24} />, title: 'Finance & Fund-Raising', desc: 'Business finance, mortgage, trade finance, and comprehensive funding solutions for growth.', link: '/services#finance' },
  ];

  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center" data-testid="home-hero">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=1920&q=80"
            alt="Dubai Skyline"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 hero-overlay"></div>
        </div>
        <div className="container-custom relative z-10">
          <h1 className="font-serif text-5xl md:text-7xl lg:text-8xl text-[#C9A962] mb-4 tracking-tight">
            FMF GLOBAL<br />CONSULTANCY
          </h1>
          <p className="text-gray-300 tracking-[0.3em] text-sm md:text-base mb-6">
            STRATEGIC PARTNER WITH FINANCE, OPERATION AND INDUSTRIAL EXPERIENCE
          </p>
          <p className="text-gray-400 text-lg md:text-xl max-w-2xl mb-12">
            Empowering businesses at every stage of growth with strategic finance, operational excellence, and industrial expertise across the UAE and GCC region.
          </p>
          <div className="flex flex-wrap gap-8">
            <div>
              <p className="font-serif text-4xl md:text-5xl text-[#C9A962]">25+</p>
              <p className="text-gray-400 text-sm">Years Experience</p>
            </div>
            <div>
              <p className="font-serif text-4xl md:text-5xl text-[#C9A962]">98%</p>
              <p className="text-gray-400 text-sm">Success Rate</p>
            </div>
          </div>
        </div>
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2">
          <div className="scroll-indicator w-6 h-10 border-2 border-white/30 rounded-full flex justify-center pt-2">
            <div className="w-1 h-2 bg-white/50 rounded-full"></div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="section-padding bg-[#1A1A1C]" data-testid="services-section">
        <div className="container-custom">
          <p className="text-[#C9A962] tracking-[0.2em] text-sm mb-4">OUR SERVICES</p>
          <h2 className="font-serif text-3xl md:text-5xl text-white mb-4">Integrated Service Ecosystem</h2>
          <p className="text-gray-400 max-w-2xl mb-12">
            360° financial support ecosystem for sustainable growth. From formation to financing and scaling, we've got you covered.
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => (
              <Link
                key={index}
                to={service.link}
                className="service-card p-6 group"
                data-testid={`service-card-${index}`}
              >
                <div className="w-12 h-12 bg-[#C9A962]/10 flex items-center justify-center mb-4 text-[#C9A962]">
                  {service.icon}
                </div>
                <h3 className="font-serif text-xl text-white mb-3 group-hover:text-[#C9A962] transition-colors">{service.title}</h3>
                <p className="text-gray-400 text-sm mb-4">{service.desc}</p>
                <span className="text-[#C9A962] text-sm flex items-center gap-2">
                  Learn More <ArrowRight size={14} />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="section-padding bg-[#232325]" data-testid="why-choose-section">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-[#C9A962] tracking-[0.2em] text-sm mb-4">WHY CHOOSE US</p>
              <h2 className="font-serif text-3xl md:text-4xl text-white mb-6">Strategic Finance with Operation & Industrial Experience</h2>
              <p className="text-gray-400 mb-8">
                With over 25 years of strategic finance leadership combined with hands-on operational and industrial experience in the UAE/GCC market, we bring unparalleled expertise to every engagement.
              </p>
              <ul className="space-y-4 mb-8">
                {[
                  'Strategic finance leadership across industrial sectors',
                  'Operational excellence in manufacturing & processing',
                  'Deep expertise in Float Glass, Aluminium & Fabrication industries',
                  'Results-driven methodology with measurable outcomes'
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <Check size={20} className="text-[#C9A962] mt-0.5 flex-shrink-0" />
                    <span className="text-gray-300">{item}</span>
                  </li>
                ))}
              </ul>
              <Link to="/about" className="btn-outline-gold inline-flex items-center gap-2">
                About Our Legacy <ArrowRight size={16} />
              </Link>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=800&q=80"
                alt="Dubai Business District"
                className="w-full h-auto"
              />
              <div className="absolute bottom-0 left-0 bg-[#1A1A1C] p-6">
                <p className="text-[#C9A962] text-xs tracking-wider mb-1">FOUNDED BY</p>
                <p className="font-serif text-white text-lg">Dr. Fathaulla Sahib F.M.</p>
                <p className="text-gray-400 text-sm">CPA (USA) | MBA | PhD</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Vision & Mission Section */}
      <section className="section-padding bg-[#1A1A1C]" data-testid="vision-mission-section">
        <div className="container-custom">
          <p className="text-[#C9A962] tracking-[0.2em] text-sm mb-4 text-center">OUR FOUNDATION</p>
          <h2 className="font-serif text-3xl md:text-4xl text-white mb-12 text-center">Vision & Mission</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-[#232325] p-8 border-l-4 border-[#C9A962]">
              <h3 className="font-serif text-2xl text-white mb-4">Our Vision</h3>
              <p className="text-gray-400">
                To become the premier provider of innovative finance, banking, tax, and corporate services, setting industry standards and empowering businesses to thrive through forward-thinking solutions.
              </p>
            </div>
            <div className="bg-[#232325] p-8 border-l-4 border-[#C9A962]">
              <h3 className="font-serif text-2xl text-white mb-4">Our Mission</h3>
              <p className="text-gray-400">
                To empower businesses at every stage of growth by delivering high-quality, precise, and results-driven services that align with long-term success and sustainable growth.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Founder Section */}
      <section className="section-padding bg-[#232325]" data-testid="founder-section">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <img
                src="https://customer-assets.emergentagent.com/job_business-online-15/artifacts/hto1ki23_WhatsApp%20Image%202026-02-19%20at%2002.02.15.jpeg"
                alt="Dr. Fathaulla Sahib F.M."
                className="w-full max-w-md mx-auto"
              />
            </div>
            <div>
              <p className="text-[#C9A962] tracking-[0.2em] text-sm mb-2">FOUNDER & CEO</p>
              <h2 className="font-serif text-3xl md:text-4xl text-white mb-2">Dr. Fathaulla Sahib F.M.</h2>
              <p className="text-[#C9A962] mb-6">CPA (USA) | MBA | PhD</p>
              <p className="text-gray-400 mb-6">
                With over 25 years of strategic finance leadership combined with hands-on operational and industrial experience across the UAE and GCC region. Working experience with Big 4 companies including E&Y, Deloitte, and KPMG.
              </p>
              <p className="text-gray-400 mb-8">
                His expertise spans strategic financial planning, industrial operations, and corporate restructuring across Float Glass manufacturing, Coater Lines & Processing Units, Aluminium extrusions, and fabrication industries.
              </p>
              <div className="flex flex-wrap gap-8 mb-8">
                <div>
                  <p className="font-serif text-3xl text-[#C9A962]">25+</p>
                  <p className="text-gray-400 text-sm">Years Experience</p>
                </div>
                <div>
                  <p className="font-serif text-3xl text-[#C9A962]">Big 4</p>
                  <p className="text-gray-400 text-sm">E&Y, Deloitte, KPMG</p>
                </div>
                <div>
                  <p className="font-serif text-3xl text-[#C9A962]">CFO</p>
                  <p className="text-gray-400 text-sm">Industrial Leadership</p>
                </div>
              </div>
              <Link to="/team" className="btn-outline-gold inline-flex items-center gap-2">
                Learn More About Our Team <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-[#1A1A1C] border-t border-[#2D2D30]" data-testid="cta-section">
        <div className="container-custom text-center">
          <p className="text-[#C9A962] tracking-[0.2em] text-sm mb-4">READY TO GET STARTED?</p>
          <h2 className="font-serif text-3xl md:text-4xl text-white mb-6">Elevate Your Financial Strategy</h2>
          <p className="text-gray-400 max-w-2xl mx-auto mb-8">
            Partner with FMF Global Consultancy for expert guidance in finance, tax, corporate services, and beyond. Let's build your success story together.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/contact" className="btn-gold" data-testid="cta-book-consultation">Book Free Consultation</Link>
            <a href="tel:+971548887455" className="btn-outline-gold">Call +971 54 888 7455</a>
          </div>
        </div>
      </section>
    </div>
  );
};

// About Page
const AboutPage = () => {
  const values = [
    { title: 'Excellence', desc: 'Delivering exceptional quality in every service, exceeding client expectations consistently.' },
    { title: 'Integrity', desc: 'Operating with transparency, honesty, and ethical practices in all our dealings.' },
    { title: 'Innovation', desc: 'Embracing forward-thinking solutions and staying ahead of industry developments.' },
    { title: 'Client Focus', desc: 'Placing our clients\' success at the center of everything we do.' },
  ];

  return (
    <div data-testid="about-page">
      <PageHero
        label="ABOUT US"
        title="A Legacy of Excellence & Innovation"
        description="For over 25 years, FMF Global Consultancy has been the trusted partner for businesses seeking comprehensive solutions in finance, tax, and corporate services across the UAE and GCC region."
      />

      {/* Our Story Section */}
      <section className="section-padding bg-[#1A1A1C]" data-testid="our-story-section">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-[#C9A962] tracking-[0.2em] text-sm mb-4">OUR STORY</p>
              <h2 className="font-serif text-3xl md:text-4xl text-white mb-6">Building Success Together</h2>
              <p className="text-gray-400 mb-4">
                FMF Global Consultancy was founded with a singular vision: to provide businesses with integrated, high-quality services that drive sustainable growth. What started as a specialized financial consulting firm has evolved into a comprehensive business solutions provider.
              </p>
              <p className="text-gray-400 mb-4">
                Our journey has been defined by our commitment to excellence, deep understanding of the local market, and unwavering dedication to our clients' success. We've helped hundreds of businesses—from startups to established enterprises—navigate the complexities of the UAE business landscape.
              </p>
              <p className="text-gray-400">
                Today, we stand as a premier provider of finance, tax, corporate, and property services, backed by a team of qualified professionals and a methodology that delivers measurable results.
              </p>
            </div>
            <div className="relative">
              <img
                src="https://customer-assets.emergentagent.com/job_funny-bell-3/artifacts/ilzqu14m_WhatsApp%20Image%202026-02-19%20at%2016.31.57.jpeg"
                alt="Industrial Operations"
                className="w-full h-auto"
              />
              <div className="absolute bottom-0 right-0 bg-[#C9A962] p-6">
                <p className="font-serif text-4xl text-[#1A1A1C]">25+</p>
                <p className="text-[#1A1A1C] text-sm">Years of Excellence</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Vision, Mission & Values Section */}
      <section className="section-padding bg-[#232325]" data-testid="values-section">
        <div className="container-custom">
          <p className="text-[#C9A962] tracking-[0.2em] text-sm mb-4 text-center">OUR FOUNDATION</p>
          <h2 className="font-serif text-3xl md:text-4xl text-white mb-12 text-center">Vision, Mission & Values</h2>
          
          <div className="grid md:grid-cols-2 gap-8 mb-12">
            <div className="bg-[#1A1A1C] p-8 border-l-4 border-[#C9A962]">
              <h3 className="font-serif text-2xl text-white mb-4">Our Vision</h3>
              <p className="text-gray-400">
                To become the premier provider of innovative finance, banking, tax, and corporate services, setting industry standards and empowering businesses to thrive through forward-thinking solutions.
              </p>
            </div>
            <div className="bg-[#1A1A1C] p-8 border-l-4 border-[#C9A962]">
              <h3 className="font-serif text-2xl text-white mb-4">Our Mission</h3>
              <p className="text-gray-400">
                To empower businesses at every stage of growth by delivering high-quality, precise, and results-driven services that align with long-term success and sustainable growth.
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <div key={index} className="bg-[#1A1A1C] p-6 text-center">
                <h4 className="font-serif text-xl text-[#C9A962] mb-3">{value.title}</h4>
                <p className="text-gray-400 text-sm">{value.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-[#1A1A1C] border-t border-[#2D2D30]">
        <div className="container-custom text-center">
          <h2 className="font-serif text-3xl md:text-4xl text-white mb-6">Ready to Partner with Us?</h2>
          <p className="text-gray-400 max-w-2xl mx-auto mb-8">
            Let's discuss how FMF Global Consultancy can help your business achieve its goals.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/contact" className="btn-gold">Get in Touch</Link>
            <Link to="/team" className="btn-outline-gold">Meet Our Team</Link>
          </div>
        </div>
      </section>
    </div>
  );
};

// Services Page
const ServicesPage = () => {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const element = document.getElementById(location.hash.slice(1));
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
    }
  }, [location]);

  const services = [
    {
      id: 'advisory',
      num: '01',
      title: 'Strategic Advisory Services',
      desc: 'Expert advisory and consulting services leveraging Big 4 experience from E&Y, Deloitte, and KPMG.',
      image: 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=800&q=80',
      items: [
        { title: 'AI Seminars & Training', desc: 'Arranging Artificial Intelligence seminars for businesses' },
        { title: 'Virtual CFO Services', desc: 'Executive-level CFO services at reasonable cost' },
        { title: 'Business Restructuring', desc: 'Strategic restructuring for optimal performance' },
        { title: 'Cost Optimization', desc: 'Identify and implement cost reduction strategies' },
        { title: 'Process Streamlining', desc: 'Streamlining business and operational processes' },
        { title: 'Profitability Enhancement', desc: 'Ideas and strategies to enhance profitability' },
        { title: 'AI Support', desc: 'Artificial Intelligence integration and support' },
        { title: 'Board Advisory', desc: 'Strategic advisory roles to the Board' },
      ]
    },
    {
      id: 'tax',
      num: '02',
      title: 'Audit & Tax Consultants Services',
      desc: 'Expert tax advisory and compliance services to keep your business fully compliant.',
      image: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=800&q=80',
      items: [
        { title: 'Accounting & Bookkeeping', desc: 'Accurate financial record management' },
        { title: 'VAT & Corporate Tax', desc: 'Registration, filing, and UAE tax compliance' },
        { title: 'Audit & AML Compliance', desc: 'Internal audit and Anti-Money Laundering registration' },
        { title: 'CFO Services', desc: 'Executive-level financial oversight and advisory' },
        { title: 'Financial Planning', desc: 'Long-term strategy for financial health' },
        { title: 'Cash Flow Management', desc: 'Efficient receivables and payables management' },
        { title: 'Regulatory Advisory', desc: 'Tax-efficient and compliant operations' },
      ]
    },
    {
      id: 'corporate',
      num: '03',
      title: 'Corporate Services',
      desc: 'End-to-end corporate solutions from company formation to strategic growth.',
      image: 'https://images.unsplash.com/photo-1560179707-f14e90ef3623?w=800&q=80',
      items: [
        { title: 'Company Formations', desc: 'LLCs, Free Zone, and Offshore entities setup' },
        { title: 'Visa & Immigration', desc: 'Employment and investor visa processing' },
        { title: 'PRO Services', desc: 'Legal, HR, and administrative support' },
        { title: 'Marketing & Branding', desc: 'Digital strategies and visual identity' },
        { title: 'Business Buy & Sale', desc: 'M&A advisory services' },
        { title: 'Document Clearing', desc: 'Government documentation processing' },
        { title: 'Launch Support', desc: 'From concept to live operations' },
        { title: 'Strategic Consulting', desc: 'Operational optimization and market strategy' },
      ]
    },
    {
      id: 'manufacturing',
      num: '04',
      title: 'Industrial & Manufacturing Expertise',
      desc: 'Specialized consulting for manufacturing, processing industries and industrial projects.',
      image: 'https://customer-assets.emergentagent.com/job_funny-bell-3/artifacts/jrppia47_industrial%20images.png',
      items: [
        { title: 'Float Glass Manufacturing', desc: 'End-to-end float glass project development and operations' },
        { title: 'Processing Plants', desc: 'Coater lines, processing units and plant commissioning' },
        { title: 'Aluminium Extrusions', desc: 'Extrusion plant setup and operational excellence' },
        { title: 'Fabrication Industries', desc: 'Metal fabrication and industrial manufacturing' },
        { title: 'Project Development', desc: 'Greenfield and expansion project management' },
        { title: 'Cost Optimization', desc: 'Plant efficiency and cost reduction strategies' },
        { title: 'Compliance Oversight', desc: 'Industrial risk and regulatory compliance' },
        { title: 'Corporate Restructuring', desc: 'Organizational optimization for industrial entities' },
      ]
    },
    {
      id: 'finance',
      num: '05',
      title: 'Finance & Fund-Raising Services',
      desc: 'Comprehensive financing solutions to fuel your business growth.',
      image: 'https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=800&q=80',
      items: [
        { title: 'Business Finance', desc: 'Capital for operational growth and strategic investments' },
        { title: 'Mortgage Finance', desc: 'Home financing with competitive rates' },
        { title: 'Trade Finance', desc: 'Import/export and supply chain financing solutions' },
        { title: 'Auto Finance', desc: 'Vehicle financing for personal and commercial use' },
        { title: 'Corporate Facilities', desc: 'Structured credit lines for enterprises' },
        { title: 'Loan Restructuring', desc: 'Expert restructuring and refinancing services' },
      ]
    }
  ];

  const methodology = [
    { num: '01', title: 'Discovery', desc: 'Understanding your unique needs and objectives' },
    { num: '02', title: 'Strategy', desc: 'Developing tailored solutions for your goals' },
    { num: '03', title: 'Execution', desc: 'Implementing with precision and expertise' },
    { num: '04', title: 'Results', desc: 'Delivering measurable outcomes and value' },
  ];

  return (
    <div data-testid="services-page">
      <PageHero
        label="OUR SERVICES"
        title="Integrated Service Ecosystem"
        description="A 360° ecosystem for sustainable growth. From business formation to financing and scaling, we provide comprehensive solutions under one roof."
      />

      {/* Services List */}
      {services.map((service, index) => (
        <section
          key={service.id}
          id={service.id}
          className={`section-padding ${index % 2 === 0 ? 'bg-[#1A1A1C]' : 'bg-[#232325]'}`}
          data-testid={`service-section-${service.id}`}
        >
          <div className="container-custom">
            <div className={`grid lg:grid-cols-2 gap-12 items-start ${index % 2 !== 0 ? 'lg:flex-row-reverse' : ''}`}>
              <div className={index % 2 !== 0 ? 'lg:order-2' : ''}>
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 bg-[#C9A962]/10 flex items-center justify-center text-[#C9A962]">
                    <Users size={24} />
                  </div>
                  <p className="text-[#C9A962] tracking-wider text-sm">SERVICE {service.num}</p>
                </div>
                <h2 className="font-serif text-3xl md:text-4xl text-white mb-4">{service.title}</h2>
                <p className="text-gray-400 mb-8">{service.desc}</p>
                <div className="grid sm:grid-cols-2 gap-4 mb-8">
                  {service.items.map((item, idx) => (
                    <div key={idx} className="border-l-2 border-[#C9A962]/30 pl-4">
                      <h4 className="text-white font-medium mb-1">{item.title}</h4>
                      <p className="text-gray-500 text-sm">{item.desc}</p>
                    </div>
                  ))}
                </div>
                <Link to="/contact" className="btn-outline-gold inline-flex items-center gap-2">
                  Inquire About This Service <ArrowRight size={16} />
                </Link>
              </div>
              <div className={index % 2 !== 0 ? 'lg:order-1' : ''}>
                <img
                  src={service.image}
                  alt={service.title}
                  className="w-full h-auto"
                />
              </div>
            </div>
          </div>
        </section>
      ))}

      {/* Methodology Section */}
      <section className="section-padding bg-[#1A1A1C] border-t border-[#2D2D30]" data-testid="methodology-section">
        <div className="container-custom">
          <p className="text-[#C9A962] tracking-[0.2em] text-sm mb-4 text-center">OUR METHODOLOGY</p>
          <h2 className="font-serif text-3xl md:text-4xl text-white mb-12 text-center">Outcome-Driven Approach</h2>
          <p className="text-gray-400 text-center max-w-2xl mx-auto mb-12">
            Our structured methodology ensures measurable results at every step.
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {methodology.map((step, index) => (
              <div key={index} className="bg-[#232325] p-6 text-center relative">
                <p className="font-serif text-5xl text-[#C9A962]/20 absolute top-4 left-4">{step.num}</p>
                <h4 className="font-serif text-xl text-white mb-3 mt-8">{step.title}</h4>
                <p className="text-gray-400 text-sm">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-[#232325]">
        <div className="container-custom text-center">
          <h2 className="font-serif text-3xl md:text-4xl text-white mb-6">Ready to Get Started?</h2>
          <p className="text-gray-400 max-w-2xl mx-auto mb-8">
            Contact us today for a free consultation and discover how our services can help your business thrive.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/contact" className="btn-gold">Book Free Consultation</Link>
            <a href="tel:+971548887455" className="btn-outline-gold">Call +971 54 888 7455</a>
          </div>
        </div>
      </section>
    </div>
  );
};

// Team Page
const TeamPage = () => {
  const [selectedMember, setSelectedMember] = useState(null);

  const teamMembers = [
    {
      id: 1,
      name: 'Dr. Fathaulla Sahib F.M.',
      role: 'FOUNDER & CEO',
      credentials: 'CPA (USA), MBA, B.Com, PhD',
      image: 'https://customer-assets.emergentagent.com/job_business-online-15/artifacts/hto1ki23_WhatsApp%20Image%202026-02-19%20at%2002.02.15.jpeg',
      bio: 'With over 25 years of strategic finance leadership combined with hands-on operational and industrial experience across the UAE and GCC region. Working experience with Big 4 companies including E&Y, Deloitte, and KPMG.',
      qualifications: [
        'CPA from Delaware State, USA',
        'MBA from India',
        'Bachelor of Commerce in Accounting, Costing, Banking & Taxation',
        'PhD from Sorban University, France'
      ],
      expertise: [
        'Strategic Finance Leadership',
        'Industrial Operations Management',
        'Float Glass & Manufacturing Industries',
        'Corporate Restructuring',
        'Big 4 Experience (E&Y, Deloitte, KPMG)'
      ],
      experience: [
        { company: 'Dubai Investments PJSC', role: 'CFO', period: '2020-2023' },
        { company: 'Glass LLC', role: 'Senior VP Finance', period: '2018-2019' },
        { company: 'Emirates Float Glass', role: 'VP Finance', period: '2009-2017' },
        { company: 'United Float Glass [KSA]', role: 'CFO', period: '2008-2009' },
        { company: 'Guardian Jubail Saudi & Guardian RAK', role: 'UAE Finance Head', period: '2001-2008' },
      ]
    },
    {
      id: 2,
      name: 'M. Ashraf Diwan',
      role: 'SENIOR ASSOCIATE PARTNER',
      credentials: 'FCA (ICAP), ACA (ICAEW)',
      image: 'https://customer-assets.emergentagent.com/job_funny-bell-3/artifacts/g033j70p_WhatsApp%20Image%20-%20ashraf.jpeg',
      bio: 'M. Ashraf Diwan is an experienced professional with over 25 years of experience, including 19 years in the UAE. He has established a notable career in corporate finance, banking, and financial leadership. A Fellow Member of ICAP and Member of ICAEW, having qualified at Ernst & Young.',
      qualifications: [
        'Fellow Member - Institute of Chartered Accountants of Pakistan (FCA - ICAP)',
        'Member - Institute of Chartered Accountants in England and Wales (ACA - ICAEW)',
        'Qualified at Ernst & Young'
      ],
      expertise: [
        'IFRS & Financial Strategy',
        'Investment Advisory',
        'Regulatory Reporting',
        'Financial Governance',
        'ERP System Implementation (SAP, Oracle, MS Navision, Orion)'
      ],
      experience: [
        { company: 'FMF Global Consultancy', role: 'Senior Associate Partner', period: 'Present' },
        { company: 'Ahmad Alagbari Chartered Accountants', role: 'Partner', period: '' },
        { company: 'Alif Group, UAE', role: 'COO', period: '' },
        { company: 'Ernst & Young', role: 'Chartered Accountant', period: '' },
      ]
    },
    {
      id: 3,
      name: 'Faisal Fathaulla',
      role: 'OPERATIONS & TECHNICAL ADVISORY LEAD',
      credentials: 'BE, MBA (USA)',
      image: 'https://customer-assets.emergentagent.com/job_business-online-15/artifacts/4r70k6uv_WhatsApp%20Image%202026-02-18%20at%2023.03.58.jpeg',
      bio: 'Faisal Fathaulla leads our operations and technical advisory division, bringing expertise in manufacturing process optimization, power systems, automation, and infrastructure strategy.',
      qualifications: [
        'BE in Engineering',
        'MBA (USA)'
      ],
      expertise: [
        'Manufacturing Process Optimization',
        'Power Systems & Automation Advisory',
        'Cost-Efficient Industrial Solutions',
        'Supply Chain & Infrastructure Strategy'
      ],
      experience: [
        { company: 'FMF Global Consultancy', role: 'Operations & Technical Advisory Lead', period: 'Present' },
        { company: 'Industrial Advisory', role: 'Manufacturing Efficiency Consulting', period: '' },
        { company: 'Infrastructure Projects', role: 'Technical Project Evaluation', period: '' },
      ]
    },
    {
      id: 4,
      name: 'Saalihah Fathaulla',
      role: 'SENIOR CONSULTANT – LEGAL & COMPLIANCE',
      credentials: 'B.Com, MBA, LLB (UK)',
      image: '',
      bio: 'Saalihah Fathaulla brings a unique combination of legal expertise and business acumen to FMF Global Consultancy. With qualifications spanning commerce, business administration, and law from prestigious institutions.',
      qualifications: [
        'Bachelor of Commerce – Mangalore University',
        'MBA – IBM Institute, Berlin (Online)',
        'LLB (Hons) – University of Leeds, UK (2:1)'
      ],
      expertise: [
        'Legal Research & Documentation',
        'Regulatory Compliance & Internal Audit',
        'Corporate Governance Advisory',
        'Risk Assessment & Process Review',
        'Stakeholder Presentation & Reporting'
      ],
      experience: [
        { company: 'FMF Global Consultancy', role: 'Consultant / Internal Auditor', period: 'Dubai' },
        { company: 'Legal Education Sector', role: 'Public Legal Awareness', period: 'UK' },
        { company: 'Internship Programs', role: 'Litigation & Business & Human Rights', period: '' },
      ]
    }
  ];

  return (
    <div data-testid="team-page">
      <PageHero
        label="OUR TEAM"
        title="Leadership & Expertise"
        description="Meet the experienced professionals behind FMF Global Consultancy's success. Our team brings decades of combined expertise with Big 4 experience from E&Y, Deloitte, and KPMG."
      />

      {/* Team Members */}
      <section className="section-padding bg-[#1A1A1C]" data-testid="team-members-section">
        <div className="container-custom">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            {teamMembers.map((member) => (
              <div
                key={member.id}
                className="team-card bg-[#232325] overflow-hidden cursor-pointer"
                onClick={() => setSelectedMember(member)}
                data-testid={`team-member-${member.id}`}
              >
                <div className="aspect-square overflow-hidden bg-[#1A1A1C]">
                  {member.image ? (
                    <img
                      src={member.image}
                      alt={member.name}
                      className="w-full h-full object-cover object-top"
                    />
                  ) : (
                    <div className="w-full aspect-square flex items-center justify-center">
                      <span className="font-serif text-6xl text-[#C9A962]">{member.name.charAt(0)}</span>
                    </div>
                  )}
                </div>
                <div className="p-6">
                  <p className="text-[#C9A962] text-xs tracking-wider mb-2">{member.role}</p>
                  <h3 className="font-serif text-xl text-white mb-1">{member.name}</h3>
                  <p className="text-gray-400 text-sm">{member.credentials}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Detailed Team Info */}
          {teamMembers.map((member, index) => (
            <div
              key={member.id}
              className={`py-16 border-t border-[#2D2D30] ${index === 0 ? '' : 'mt-8'}`}
              id={`member-${member.id}`}
            >
              <div className="grid lg:grid-cols-3 gap-12">
                <div className="lg:col-span-1">
                  <p className="text-[#C9A962] text-xs tracking-wider mb-2">{member.role}</p>
                  <h2 className="font-serif text-3xl text-white mb-2">{member.name}</h2>
                  <p className="text-[#C9A962] mb-4">{member.credentials}</p>
                  <p className="text-gray-400">{member.bio}</p>
                </div>
                <div>
                  <h4 className="text-white font-medium mb-4">Qualifications</h4>
                  <ul className="space-y-2 mb-8">
                    {member.qualifications.map((qual, idx) => (
                      <li key={idx} className="text-gray-400 text-sm flex items-start gap-2">
                        <Check size={16} className="text-[#C9A962] mt-0.5 flex-shrink-0" />
                        {qual}
                      </li>
                    ))}
                  </ul>
                  <h4 className="text-white font-medium mb-4">Core Expertise</h4>
                  <div className="flex flex-wrap gap-2">
                    {member.expertise.map((exp, idx) => (
                      <span key={idx} className="text-xs bg-[#C9A962]/10 text-[#C9A962] px-3 py-1">
                        {exp}
                      </span>
                    ))}
                  </div>
                </div>
                <div>
                  <h4 className="text-white font-medium mb-4">Professional Experience</h4>
                  <div className="space-y-4">
                    {member.experience.map((exp, idx) => (
                      <div key={idx} className="timeline-item pb-4">
                        <p className="text-white font-medium">{exp.company}</p>
                        <p className="text-[#C9A962] text-sm">{exp.role}</p>
                        {exp.period && <p className="text-gray-500 text-xs">{exp.period}</p>}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-[#232325]">
        <div className="container-custom text-center">
          <h2 className="font-serif text-3xl md:text-4xl text-white mb-6">Ready to Work with Us?</h2>
          <p className="text-gray-400 max-w-2xl mx-auto mb-8">
            Let's discuss how FMF Global Consultancy can help your business achieve its goals.
          </p>
          <Link to="/contact" className="btn-gold">Book a Consultation</Link>
        </div>
      </section>
    </div>
  );
};

// Blog Page
const BlogPage = () => {
  const [posts, setPosts] = useState([]);
  const [activeCategory, setActiveCategory] = useState('All');
  const [loading, setLoading] = useState(true);

  const categories = ['All', 'Tax Advisory', 'Finance', 'Corporate Services', 'Property'];

  useEffect(() => {
    fetchPosts();
  }, [activeCategory]);

  const fetchPosts = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/blog`, {
        params: activeCategory !== 'All' ? { category: activeCategory } : {}
      });
      setPosts(response.data);
    } catch (error) {
      console.error('Error fetching blog posts:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="blog-page">
      <PageHero
        label="BLOG & INSIGHTS"
        title="Industry Insights & Updates"
        description="Stay informed with the latest news, regulatory updates, and expert insights on finance, tax, corporate services, and the UAE business landscape."
      />

      <section className="section-padding bg-[#1A1A1C]" data-testid="blog-list-section">
        <div className="container-custom">
          {/* Category Filter */}
          <div className="flex flex-wrap gap-2 mb-12" data-testid="category-filter">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setActiveCategory(category)}
                className={`px-4 py-2 text-sm transition-colors ${
                  activeCategory === category
                    ? 'bg-[#C9A962] text-[#1A1A1C]'
                    : 'bg-[#232325] text-gray-400 hover:text-white'
                }`}
                data-testid={`category-${category.toLowerCase().replace(' ', '-')}`}
              >
                {category}
              </button>
            ))}
          </div>

          {/* Blog Posts Grid */}
          {loading ? (
            <div className="text-center py-12">
              <p className="text-gray-400">Loading posts...</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {posts.map((post) => (
                <Link
                  key={post.id}
                  to={`/blog/${post.id}`}
                  className="blog-card bg-[#232325] group"
                  data-testid={`blog-post-${post.id}`}
                >
                  <div className="aspect-video overflow-hidden">
                    <img
                      src={post.image}
                      alt={post.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-4 mb-3">
                      <span className="text-[#C9A962] text-xs">{post.category}</span>
                    </div>
                    <p className="text-gray-500 text-xs mb-2">{post.date} • {post.author}</p>
                    <h3 className="font-serif text-xl text-white mb-3 group-hover:text-[#C9A962] transition-colors">
                      {post.title}
                    </h3>
                    <p className="text-gray-400 text-sm mb-4">{post.excerpt}</p>
                    <span className="text-[#C9A962] text-sm flex items-center gap-2">
                      Read More <ArrowRight size={14} />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          )}

          {/* Newsletter Section */}
          <div className="mt-16 bg-[#232325] p-8 text-center">
            <h3 className="font-serif text-2xl text-white mb-4">Stay Updated</h3>
            <p className="text-gray-400 mb-6">
              Subscribe to our newsletter for the latest insights on UAE business regulations, tax updates, and industry trends.
            </p>
            <Link to="/contact" className="btn-gold">Subscribe Now</Link>
          </div>
        </div>
      </section>
    </div>
  );
};

// Blog Post Detail Page
const BlogPostPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPost();
  }, [id]);

  const fetchPost = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/blog/${id}`);
      setPost(response.data);
    } catch (error) {
      console.error('Error fetching blog post:', error);
      navigate('/blog');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#1A1A1C] flex items-center justify-center pt-20">
        <p className="text-gray-400">Loading...</p>
      </div>
    );
  }

  if (!post) return null;

  return (
    <div data-testid="blog-post-page">
      <section className="pt-32 pb-16 bg-[#1A1A1C]">
        <div className="container-custom max-w-4xl">
          <Link to="/blog" className="text-[#C9A962] text-sm flex items-center gap-2 mb-8 hover:underline">
            <ChevronRight size={16} className="rotate-180" /> Back to Blog
          </Link>
          <span className="text-[#C9A962] text-sm">{post.category}</span>
          <h1 className="font-serif text-3xl md:text-5xl text-white mt-4 mb-6">{post.title}</h1>
          <p className="text-gray-500">{post.date} • {post.author}</p>
        </div>
      </section>

      <section className="pb-16 bg-[#1A1A1C]">
        <div className="container-custom max-w-4xl">
          <img
            src={post.image}
            alt={post.title}
            className="w-full h-auto mb-12"
          />
          <div className="prose prose-invert max-w-none">
            {post.content.split('\n\n').map((paragraph, index) => {
              if (paragraph.startsWith('**') && paragraph.endsWith('**')) {
                return <h3 key={index} className="font-serif text-xl text-white mt-8 mb-4">{paragraph.replace(/\*\*/g, '')}</h3>;
              }
              if (paragraph.startsWith('- ')) {
                return (
                  <ul key={index} className="space-y-2 mb-4">
                    {paragraph.split('\n').map((item, idx) => (
                      <li key={idx} className="text-gray-400 flex items-start gap-2">
                        <Check size={16} className="text-[#C9A962] mt-1 flex-shrink-0" />
                        {item.replace('- ', '')}
                      </li>
                    ))}
                  </ul>
                );
              }
              return <p key={index} className="text-gray-400 mb-4">{paragraph}</p>;
            })}
          </div>
        </div>
      </section>

      <section className="py-16 bg-[#232325]">
        <div className="container-custom text-center">
          <h3 className="font-serif text-2xl text-white mb-4">Need Expert Guidance?</h3>
          <p className="text-gray-400 mb-6">Contact FMF Global Consultancy for professional advice tailored to your business needs.</p>
          <Link to="/contact" className="btn-gold">Get in Touch</Link>
        </div>
      </section>
    </div>
  );
};

// Contact Page
const ContactPage = () => {
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    company: '',
    service: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitted(true);
      setFormData({
        full_name: '',
        email: '',
        phone: '',
        company: '',
        service: '',
        message: ''
      });
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('There was an error submitting your message. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const services = [
    'Audit & Tax Consultants',
    'Corporate Services',
    'Industrial & Manufacturing',
    'Finance & Fund-Raising',
    'Other'
  ];

  return (
    <div data-testid="contact-page">
      <PageHero
        label="CONTACT US"
        title="Get in Touch"
        description="Ready to discuss your business needs? Our team is here to help you navigate your path to success."
      />

      <section className="section-padding bg-[#1A1A1C]" data-testid="contact-form-section">
        <div className="container-custom">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Contact Form */}
            <div className="lg:col-span-2 bg-[#232325] p-8">
              <h2 className="font-serif text-2xl text-white mb-8">Send Us a Message</h2>
              
              {submitted ? (
                <div className="text-center py-12">
                  <div className="w-20 h-20 bg-[#C9A962]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Check size={40} className="text-[#C9A962]" />
                  </div>
                  <h3 className="font-serif text-2xl text-white mb-3">Thank You!</h3>
                  <p className="text-[#C9A962] text-lg mb-2">Your message has been received.</p>
                  <p className="text-gray-400 mb-8">We appreciate you reaching out to FMF Global Consultancy. Our team will review your inquiry and get back to you within 24-48 business hours.</p>
                  <div className="bg-[#1A1A1C] p-4 rounded mb-6">
                    <p className="text-gray-500 text-sm">In the meantime, feel free to reach us directly:</p>
                    <p className="text-[#C9A962]">+971 54 888 7455</p>
                  </div>
                  <button
                    onClick={() => setSubmitted(false)}
                    className="btn-outline-gold"
                  >
                    Send Another Message
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} data-testid="contact-form">
                  <div className="grid md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <label className="block text-gray-400 text-sm mb-2">Full Name *</label>
                      <input
                        type="text"
                        name="full_name"
                        value={formData.full_name}
                        onChange={handleChange}
                        required
                        placeholder="John Doe"
                        className="w-full px-4 py-3"
                        data-testid="input-full-name"
                      />
                    </div>
                    <div>
                      <label className="block text-gray-400 text-sm mb-2">Email Address *</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        placeholder="john@example.com"
                        className="w-full px-4 py-3"
                        data-testid="input-email"
                      />
                    </div>
                  </div>
                  <div className="grid md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <label className="block text-gray-400 text-sm mb-2">Phone Number</label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="+971 XX XXX XXXX"
                        className="w-full px-4 py-3"
                        data-testid="input-phone"
                      />
                    </div>
                    <div>
                      <label className="block text-gray-400 text-sm mb-2">Company Name</label>
                      <input
                        type="text"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        placeholder="Your Company"
                        className="w-full px-4 py-3"
                        data-testid="input-company"
                      />
                    </div>
                  </div>
                  <div className="mb-6">
                    <label className="block text-gray-400 text-sm mb-2">Service of Interest</label>
                    <select
                      name="service"
                      value={formData.service}
                      onChange={handleChange}
                      className="w-full px-4 py-3"
                      data-testid="input-service"
                    >
                      <option value="">Select a service</option>
                      {services.map((service) => (
                        <option key={service} value={service}>{service}</option>
                      ))}
                    </select>
                  </div>
                  <div className="mb-6">
                    <label className="block text-gray-400 text-sm mb-2">Your Message *</label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={5}
                      placeholder="How can we help you?"
                      className="w-full px-4 py-3 resize-none"
                      data-testid="input-message"
                    ></textarea>
                  </div>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="btn-gold w-full md:w-auto"
                    data-testid="submit-button"
                  >
                    {submitting ? 'Sending...' : 'Send Message'}
                  </button>
                </form>
              )}
            </div>

            {/* Contact Info */}
            <div className="space-y-6">
              <div className="bg-[#232325] p-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#C9A962]/10 flex items-center justify-center flex-shrink-0">
                    <MapPin size={20} className="text-[#C9A962]" />
                  </div>
                  <div>
                    <h4 className="text-white font-medium mb-1">Office Address</h4>
                    <p className="text-gray-400 text-sm">Office No. 310, Bahwan Tower,<br />Al Nahda 1, Dubai, UAE</p>
                  </div>
                </div>
              </div>

              <div className="bg-[#232325] p-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#C9A962]/10 flex items-center justify-center flex-shrink-0">
                    <Phone size={20} className="text-[#C9A962]" />
                  </div>
                  <div>
                    <h4 className="text-white font-medium mb-1">Phone / WhatsApp</h4>
                    <a href="tel:+971548887455" className="text-gray-400 text-sm hover:text-[#C9A962]">+971 54 888 7455</a>
                  </div>
                </div>
              </div>

              <div className="bg-[#232325] p-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#C9A962]/10 flex items-center justify-center flex-shrink-0">
                    <Mail size={20} className="text-[#C9A962]" />
                  </div>
                  <div>
                    <h4 className="text-white font-medium mb-1">Email</h4>
                    <a href="mailto:info@fmfglobalconsultancy.com" className="text-gray-400 text-sm hover:text-[#C9A962] block">info@fmfglobalconsultancy.com</a>
                  </div>
                </div>
              </div>

              <div className="bg-[#232325] p-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#C9A962]/10 flex items-center justify-center flex-shrink-0">
                    <Clock size={20} className="text-[#C9A962]" />
                  </div>
                  <div>
                    <h4 className="text-white font-medium mb-1">Business Hours</h4>
                    <p className="text-gray-400 text-sm">Sunday - Thursday<br />9:00 AM - 6:00 PM</p>
                  </div>
                </div>
              </div>

              {/* Map placeholder */}
              <div className="bg-[#232325] p-6">
                <div className="aspect-video bg-[#1A1A1C] flex items-center justify-center">
                  <p className="text-gray-500 text-sm">Bahwan Tower, Al Nahda 1, Dubai</p>
                </div>
              </div>

              <div className="bg-[#232325] p-6 text-center">
                <h4 className="text-white font-medium mb-4">Need Immediate Assistance?</h4>
                <p className="text-gray-400 text-sm mb-4">Call us directly or send a WhatsApp message.</p>
                <div className="flex gap-4 justify-center">
                  <a href="tel:+971548887455" className="btn-gold text-sm">Call Now</a>
                  <a
                    href="https://wa.me/971548887455?text=Hello!%20I%20would%20like%20to%20inquire%20about%20your%20services."
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-outline-gold text-sm"
                  >
                    WhatsApp Us
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

// Main App Component
function App() {
  return (
    <BrowserRouter>
      <div className="App min-h-screen bg-[#1A1A1C]">
        <Navigation />
        <main>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/services" element={<ServicesPage />} />
            <Route path="/team" element={<TeamPage />} />
            <Route path="/blog" element={<BlogPage />} />
            <Route path="/blog/:id" element={<BlogPostPage />} />
            <Route path="/contact" element={<ContactPage />} />
          </Routes>
        </main>
        <Footer />
        <WhatsAppFloat />
      </div>
    </BrowserRouter>
  );
}

export default App;
